import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  getAllCountries,
  getCouncils,
  createCountry,
  updateCountry,
  deactivateCountry,
  createCouncil,
  updateCouncil,
  deactivateCouncil,
  AdminCountryDTO,
  AdminCouncilDTO,
  AdminBoardDTO,
  getBoards,
  createBoard,
  updateBoard,
} from '../services/adminLocationService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const LocationManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<AdminCountryDTO[]>([]);
  const [councils, setCouncils] = useState<AdminCouncilDTO[]>([]);
  const [boards, setBoards] = useState<AdminBoardDTO[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<AdminCountryDTO | AdminCouncilDTO | AdminBoardDTO | null>(null);
  const [itemType, setItemType] = useState<'COUNTRY' | 'COUNCIL' | 'BOARD'>('COUNTRY');
  const [formName, setFormName] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formCountryId, setFormCountryId] = useState<number | ''>('');
  const [formLevel, setFormLevel] = useState('NATIONAL');
  const [formRegionId, setFormRegionId] = useState<string>('');
  const [filterCountryId, setFilterCountryId] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadData();
  }, [tabValue, filterCountryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Always load countries for dropdowns
      const countryData = await getAllCountries();
      setCountries(countryData);

      if (tabValue === 0) {
        // Countries already loaded
      } else if (tabValue === 1) {
        const data = await getCouncils();
        setCouncils(data);
      } else if (tabValue === 2) {
        const data = await getBoards(filterCountryId ? (filterCountryId as number) : undefined);
        setBoards(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setFilterCountryId('');
    setSearchText('');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: AdminCountryDTO | AdminCouncilDTO | AdminBoardDTO, type: 'COUNTRY' | 'COUNCIL' | 'BOARD') => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
    setItemType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setItemType(tabValue === 0 ? 'COUNTRY' : tabValue === 1 ? 'COUNCIL' : 'BOARD');
    setFormName('');
    setFormCode('');
    setFormCountryId('');
    setFormLevel('NATIONAL');
    setFormRegionId('');
    setOpenDialog(true);
  };

  const handleEdit = () => {
    if (selectedItem) {
      setFormName(selectedItem.name || '');
      setFormCode(selectedItem.code || '');
      if (itemType === 'COUNCIL') {
        setFormCountryId((selectedItem as AdminCouncilDTO).countryId || '');
      } else if (itemType === 'BOARD') {
        const board = selectedItem as AdminBoardDTO;
        setFormCountryId(board.countryId || '');
        setFormLevel(board.level || 'NATIONAL');
        setFormRegionId(board.regionId || '');
      }
    }
    setAnchorEl(null);
    setOpenDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedItem || !selectedItem.id) return;

    try {
      setLoading(true);
      setError(null);
      if (itemType === 'COUNTRY') {
        await deactivateCountry(selectedItem.id);
      } else if (itemType === 'COUNCIL') {
        await deactivateCouncil(selectedItem.id);
      } else if (itemType === 'BOARD') {
        // Board delete not implemented yet, just close menu
      }
      await loadData();
      handleMenuClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (itemType === 'COUNTRY') {
        const countryData: AdminCountryDTO = {
          name: formName,
          code: formCode,
        };
        if (selectedItem?.id) await updateCountry(selectedItem.id, countryData);
        else await createCountry(countryData);

      } else if (itemType === 'COUNCIL') {
        const councilData: AdminCouncilDTO = {
          name: formName,
          code: formCode,
          countryId: Number(formCountryId),
        };
        if (selectedItem?.id) await updateCouncil(selectedItem.id, councilData);
        else await createCouncil(councilData);

      } else if (itemType === 'BOARD') {
        const boardData: AdminBoardDTO = {
          name: formName,
          code: formCode,
          countryId: Number(formCountryId),
          level: formLevel,
          regionId: formRegionId,
        };
        if (selectedItem?.id) await updateBoard(selectedItem.id!, boardData);
        else await createBoard(boardData);
      }

      await loadData();
      handleDialogClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormName('');
    setFormCode('');
    setFormCountryId('');
    setFormLevel('NATIONAL');
    setFormRegionId('');
  };

  // Filter lists based on search text
  const filteredCountries = countries.filter(i => i.name.toLowerCase().includes(searchText.toLowerCase()));
  const filteredCouncils = councils.filter(i => i.name.toLowerCase().includes(searchText.toLowerCase()));
  const filteredBoards = boards.filter(i => i.name.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <LocationIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Location Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add {tabValue === 0 ? 'Country' : tabValue === 1 ? 'Council' : 'Board'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Countries" />
          <Tab label="Councils" />
          <Tab label="Boards" />
        </Tabs>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: 250 }}
          />
          {tabValue === 2 && (
            <TextField
              size="small"
              select
              label="Filter by Country"
              value={filterCountryId}
              onChange={(e) => setFilterCountryId(e.target.value ? Number(e.target.value) : '')}
              sx={{ width: 200 }}
              SelectProps={{ native: true }}
            >
              <option value="">All Countries</option>
              {countries.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </TextField>
          )}
        </Box>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCountries.map((country) => (
                  <TableRow key={country.id}>
                    <TableCell>{country.id}</TableCell>
                    <TableCell>{country.name}</TableCell>
                    <TableCell>{country.code || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={country.status || 'ACTIVE'}
                        color={country.status === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, country, 'COUNTRY')}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCouncils.map((council) => (
                  <TableRow key={council.id}>
                    <TableCell>{council.id}</TableCell>
                    <TableCell>{council.name}</TableCell>
                    <TableCell>{council.code || '-'}</TableCell>
                    <TableCell>{council.countryName || `Country ID: ${council.countryId}`}</TableCell>
                    <TableCell>
                      <Chip
                        label={council.status || 'ACTIVE'}
                        color={council.status === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, council, 'COUNCIL')}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBoards.map((board) => (
                  <TableRow key={board.id}>
                    <TableCell>{board.id}</TableCell>
                    <TableCell>{board.name}</TableCell>
                    <TableCell>{board.code || '-'}</TableCell>
                    <TableCell>{board.countryName || board.countryId}</TableCell>
                    <TableCell>{board.level}</TableCell>
                    <TableCell>{board.regionId || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, board, 'BOARD')}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedItem ? `Edit ${itemType.charAt(0) + itemType.slice(1).toLowerCase()}` : `Add New ${itemType.charAt(0) + itemType.slice(1).toLowerCase()}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <TextField
              label="Code"
              fullWidth
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
            />
            {itemType !== 'COUNTRY' && (
              <TextField
                select
                label="Country"
                fullWidth
                required
                value={formCountryId}
                onChange={(e) => setFormCountryId(Number(e.target.value))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Country</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </TextField>
            )}

            {itemType === 'BOARD' && (
              <>
                <TextField
                  select
                  label="Level"
                  fullWidth
                  value={formLevel}
                  onChange={(e) => setFormLevel(e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="NATIONAL">NATIONAL</option>
                  <option value="STATE">STATE</option>
                  <option value="COUNCIL">COUNCIL</option>
                </TextField>
                <TextField
                  label="Region ID"
                  fullWidth
                  value={formRegionId}
                  onChange={(e) => setFormRegionId(e.target.value)}
                  helperText="Optional region identifier"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        {itemType !== 'BOARD' && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Container>
  );
};
export default LocationManagement;

